import { useMutation } from "@tanstack/react-query"
import axios from "axios"
import { DictType } from "@pma/types"
import { CREATE_TASK_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { PlusCircleIcon } from "lucide-react"
import { Button } from "@components/common/button"
import { Popover, PopoverContent, PopoverTrigger } from "@components/common/popover"
import { useContext, useState } from "react"
import { Input } from "@components/common/input"
import { Separator } from "@components/common/separator"
import { toast } from "@hooks/use-toast"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import useTranslator from "@hooks/use-translator"
import { GroupedTasksContext } from "./grouped-tasks/context/GroupedTasksProvider"

interface Props {
  task: DictType
  revalidate?: CallableFunction
}

interface Task {
  id: number
  tasks_group_id: number | null
  title?: string
  created_at?: string | number | Date
  _subtasks?: Subtask[]
  [key: string]: any
}

interface Subtask {
  id: number
  parent_tasks_id: number
  [key: string]: any
}

interface Group {
  id: number
  name: string
  project_id: number
  user_id: number
  _tasks: Task[]
  created_at: string | null
}

const AddSubItem = ({ task }: Props) => {
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const [subItem, setSubItem] = useState<DictType>({})
  const [isOpen, setIsOpen] = useState(false)

  const isSubItem = task && task?.id

  const { groupedTasks, setGroupedTasks } = useContext(GroupedTasksContext) as {
    groupedTasks: Group[]
    setGroupedTasks: (group: Group[]) => void
  }

  const { translate } = useTranslator()
  const navigate = useNavigate()
  const [searchParam] = useSearchParams()
  const newParams = new URLSearchParams(searchParam)

  const currentTime = Date.now()

  const midnightTime = new Date().setHours(23, 59, 59, 999);

  const addSubItem = async () => {
    const response = await axios.post(
      SERVER_URL + CREATE_TASK_ENDPOINT,
      {
        parent_tasks_id: task.id,
        title: subItem?.title,
        start_at: currentTime,
        finish_to: midnightTime,
        project_id: task?.project_id,
        tasks_group_id: task?.group_id > 0 ? task?.group_id : 0,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response?.data
  }

  const addSubItemMutation = useMutation({
    mutationKey: ["add-sub-item", task?.id],
    mutationFn: addSubItem,
    onSuccess: (data) => {
      const newGroupedTasks = groupedTasks.map((group) => {
        if (group?.id == task?.tasks_group_id) {
          const newTaskList = group._tasks.map((task) => {
            if (task.id == isSubItem) {
              task._subtasks = [...(task._subtasks == undefined ? [] : task._subtasks), data]
            }
            return task
          })
          group._tasks = newTaskList
        }

        return group
      })
      setGroupedTasks(newGroupedTasks)
      // Revalidate project/tasks when tasks and project is updated
      newParams.set("revalidate", "tasks")
      navigate(`?${newParams.toString()}`)
      toast({
        title: translate("Success"),
        description: translate("Subitem added"),
        variant: "success",
      })

      setSubItem({})
      setIsOpen(false)
    },
    onError: (error: DictType) => {
      const message = error?.response.data.message
      toast({
        title: translate("Error"),
        description: message || translate("Error adding subitem"),
        variant: "error",
      })
    },
  })

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <Tooltip>
        <TooltipTrigger>
          <PopoverTrigger asChild>
            <Button variant={"ghost"} size={"icon"} className='h-6 w-6 rounded-full'>
              <PlusCircleIcon className='text-primary' size={14} />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>{translate("Add SubItem")}</TooltipContent>
      </Tooltip>
      <PopoverContent className=''>
        <div className='space-y-2'>
          <h4 className='text-sm font-semibold'>
            {translate("Add SubItem to")} {task?.title}
          </h4>
          <Separator />
          <div className='w-full'>
            <div className='flex w-full flex-col gap-4'>
              <Input
                placeholder={translate("SubItem")}
                className='h-12 w-full'
                onChange={(e: any) => setSubItem((prev) => ({ ...prev, title: e.target.value }))}
              />
            </div>
          </div>
        </div>
        <Button
          className='my-2 w-full'
          size={"sm"}
          loading={addSubItemMutation?.isPending}
          disabled={addSubItemMutation?.isPending || !subItem?.title}
          onClick={() => addSubItemMutation.mutate()}
        >
          {translate("Add")}
        </Button>
      </PopoverContent>
    </Popover>
  )
}

export default AddSubItem
